<template>
   <div class="flex flex-col gap-y-[24px]">

    <div class="bg-surface-primary p-[16px] rounded-[8px]">
      <SelectAlgoPart
        :stepFormData="stepFormData"
        :typeVariants="typeVariants"
        :availableAlgorithms="availableAlgorithms"
        :isAlgoSelectorDisabled="isAlgoSelectorDisabled"
        @on-type-change="onTypeChange"
        @on-algorithm-change="onAlgorithmChange"
      />
    </div>


      <div class="flex gap-x-[8px]">
        <VButton type="light" label="Назад" @click="prevStepHandler" size="m" />
        <VButton type="primary" label="Далее" @click="nextStepHandler" size="m" :disabled="isNextButtonDisabled" />
      </div>
  </div>
</template>

<script>
import { VButton } from '@gostelemed.front/rt-doctis.designsystem';
import kzsService from '@/services/kzs/kzs.service';
import SelectAlgoPart from './FormParts/SelectAlgoPart.vue';

export default {
  components: {
    VButton,
    SelectAlgoPart
  },
  name: 'CreateKzsFormStep2',
  props: {
    fullKzsFormData: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  data() {
    return {
      stepFormData: {
        type: null,
        algorithm: null,
      },
      typeVariants: [
        {
          id: 0,
          title: 'Тревожный',
        },
        {
          id: 1,
          title: 'Успешный',
        },
      ],
      availableAlgorithms: [],
      isAlgoFetched: false,
    };
  },
  mounted() {
    this.parseFullKzsFormData();
  },
  computed: {
    isNextButtonDisabled() {
      return !this.stepFormData.algorithm;
    },
    isAlgoSelectorDisabled() {
      return !this.stepFormData.type || !this.isAlgoFetched;
    },
  },
  methods: {
    getAlgorithmsCacheKey() {
      const typeId = this.stepFormData.type?.id;
      const subType = this.fullKzsFormData?.subType;
      if (typeId === null || typeof typeId === 'undefined') return null;
      return `kzs-available-algorithms:${typeId}:${subType ?? 'null'}`;
    },
    getAlgorithmsFromCache(cacheKey) {
      if (!cacheKey) return null;

      try {
        const cachedValue = sessionStorage.getItem(cacheKey);
        if (cachedValue === null) return null;
        const parsed = JSON.parse(cachedValue);
        return Array.isArray(parsed) ? parsed : null;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    saveAlgorithmsToCache(cacheKey, algorithms) {
      if (!cacheKey) return;

      try {
        sessionStorage.setItem(cacheKey, JSON.stringify(algorithms));
      } catch (error) {
        console.error(error);
      }
    },
    mapAlgorithms(response) {
      return response?.items.map((item, idx) => ({
        ...item,
        id: idx,
        title: item.name,
      })) || [];
    },
    applyAlgorithms(algorithms) {
      this.availableAlgorithms = algorithms;

      if(this.fullKzsFormData?.algorithm?.type) {
        const algorithm = this.availableAlgorithms.find(item => item.algorithmType === this.fullKzsFormData?.algorithm?.type);
        if(algorithm) {
          this.stepFormData.algorithm = algorithm;
        }
      }

      if (this.availableAlgorithms.length === 1) {
        this.stepFormData.algorithm = this.availableAlgorithms[0];
      }
    },
    onAlgorithmChange(value) {
      this.stepFormData.algorithm = value;
      this.saveStepResult({ resetAlgorithmParams: true });
    },
    async onTypeChange(value) {
      this.stepFormData.type = value;
      this.stepFormData.algorithm = null;
      await this.fetchAvailableAlgorithms();
    },
    // Запрос списка доступных алгоритмов
    async fetchAvailableAlgorithms() {
      this.isAlgoFetched = false;
      const cacheKey = this.getAlgorithmsCacheKey();
      try {
        const cachedAlgorithms = this.getAlgorithmsFromCache(cacheKey);
        if (cachedAlgorithms !== null) {
          this.applyAlgorithms(cachedAlgorithms);
          return;
        }

        const data = await kzsService.getAvailableAlgorithms({
          KzsType: this.stepFormData.type?.id,
          KzsSubType: this.fullKzsFormData.subType,
        });
        const algorithms = this.mapAlgorithms(data);
        this.applyAlgorithms(algorithms);
        this.saveAlgorithmsToCache(cacheKey, algorithms);
      } catch (error) {
        console.error(error);
      } finally {
        this.isAlgoFetched = true;
      }
    },
    // Парсинг данных из fullKzsFormData для рендера ,
    // если мы вернулись на этот шаг после перехода на другой шаг
    parseFullKzsFormData() {
      if (!this.fullKzsFormData) return;
      this.stepFormData = {
        ...this.stepFormData,
        ...this.fullKzsFormData,
      };

      if (this.stepFormData.type) {
        this.fetchAvailableAlgorithms();
      }
    },
    // Сохранение данных для передачи на следующий шаг
    saveStepResult({ resetAlgorithmParams = false } = {}) {
      const data = {
        algorithm: {
          type: this.stepFormData?.algorithm?.algorithmType,
          params: resetAlgorithmParams ? {} : (this.fullKzsFormData?.algorithm?.params || {}),
        },
        type: this.stepFormData?.type,
      }
      this.$emit('on-save-data', data);
      this.$emit('on-save-algorithm-data', this.stepFormData?.algorithm);
    },
    // Переход на предыдущий шаг
    prevStepHandler() {
      this.saveStepResult();
      this.$emit('on-prev-step');
    },
    // Переход на следующий шаг
    nextStepHandler() {
      this.saveStepResult();
      this.$emit('on-next-step');
    },
  },
}
</script>
